import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell, AcsStatusBadge } from '../components';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n/I18nProvider';
import {
  api,
  clearEventBanner,
  uploadEventBanner,
  type DeviceSummary,
  type EventDetail,
  type RequestableItem,
} from '../api/client';

/**
 * Dettaglio della fiera: informativa attiva, stato e tablet associati.
 *
 * È la schermata che rende operativa una fiera senza passare dalla riga di
 * comando. Le regole privacy restano lato server — qui vengono solo rese
 * visibili: in particolare il motivo per cui l'attivazione è bloccata finché
 * manca un'informativa pubblicata (§8, §19).
 */
export function EventDetailPage() {
  const { id = '' } = useParams();
  const auth = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const event = useQuery({ queryKey: ['event', id], queryFn: () => api.getEvent(id) });
  const policies = useQuery({ queryKey: ['policies'], queryFn: api.listPolicies });
  const devices = useQuery({ queryKey: ['devices', id], queryFn: () => api.listDevices(id) });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['event', id] }),
      queryClient.invalidateQueries({ queryKey: ['devices', id] }),
      queryClient.invalidateQueries({ queryKey: ['events'] }),
    ]);
  };

  const updateEvent = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateEvent(id, patch),
    onSuccess: () => void refresh(),
    onError: (cause: Error) => setError(cause.message),
  });

  const registerDevice = useMutation({
    mutationFn: (label: string) => api.registerDevice(id, label),
    onSuccess: (result) => {
      // Mostrato una sola volta: il server conserva solo l'hash del token.
      setIssuedToken(result.pairingToken);
      setDeviceLabel('');
      setCopied(false);
      void refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const revokeDevice = useMutation({
    mutationFn: (deviceId: string) => api.revokeDevice(deviceId),
    onSuccess: () => void refresh(),
    onError: (cause: Error) => setError(cause.message),
  });

  /**
   * Rigenerazione del codice.
   *
   * Il token non è recuperabile — nel database c'è solo l'hash — quindi quando
   * serve di nuovo lo si sostituisce. Il precedente smette subito di
   * funzionare, il che rende l'operazione utile anche in caso di sospetta fuga.
   */
  const reissueToken = useMutation({
    mutationFn: (deviceId: string) => api.reissueDeviceToken(deviceId),
    onSuccess: (result) => {
      setIssuedToken(result.pairingToken);
      setCopied(false);
      void refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  if (event.isLoading) return <p style={{ padding: '2rem' }}>{t.common.loading}</p>;
  if (event.isError || !event.data) return <p style={{ padding: '2rem' }}>{t.errors.unexpected}</p>;

  const detail = event.data;
  const publishedPolicies = (policies.data?.policies ?? []).filter((p) => p.published);
  const activePolicy = publishedPolicies.find((p) => p.id === detail.activePrivacyPolicyId);
  const canActivate = detail.activePrivacyPolicyId !== null;

  function handleRegisterDevice(submitEvent: FormEvent): void {
    submitEvent.preventDefault();
    setError(null);
    if (deviceLabel.trim().length > 0) registerDevice.mutate(deviceLabel.trim());
  }

  return (
    <AcsPageShell
      header={
        <>
          <AcsLogo />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--acs-space-4)' }}>
            <LanguageSwitcher />
            <AcsButton variant="secondary" onClick={() => void auth.logout()}>
              {t.app.logout}
            </AcsButton>
          </div>
        </>
      }
    >
      <Link to="/app" style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--acs-space-4)', flexWrap: 'wrap' }}>
        <h1 style={{ marginBottom: 0 }}>{detail.name}</h1>
        <AcsStatusBadge>{t.app.eventStatus[detail.status]}</AcsStatusBadge>
      </div>
      <p style={{ color: 'var(--acs-color-text-muted)', marginTop: 0 }}>
        {new Date(detail.startsAt).toLocaleDateString()} —{' '}
        {new Date(detail.endsAt).toLocaleDateString()}
        {detail.location ? ` · ${detail.location}` : ''} · {detail.languages.join(', ').toUpperCase()}
      </p>

      {error ? (
        <p className="acs-field__error" role="alert">
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap', margin: 'var(--acs-space-4) 0' }}>
        <Link to={`/app/events/${id}/preview`} className="acs-button acs-button--secondary">
          {t.eventDetail.preview}
        </Link>
        <Link to={`/app/events/${id}/contacts`} className="acs-button acs-button--secondary">
          {t.eventDetail.contacts}
        </Link>
      </div>

      <div className="acs-stack">
        {/* ---------------------------------------------------- Immagine -- */}
        <BrandingSection event={detail} onChanged={() => void refresh()} />

        {/* ------------------------------------------- Voci richiedibili -- */}
        <RequestableItemsSection event={detail} onChanged={() => void refresh()} />

        {/* ------------------------------------ Etichetta del ricontatto -- */}
        <CallbackLabelSection event={detail} onChanged={() => void refresh()} />

        {/* ------------------------------------------------ Informativa -- */}
        <AcsCard>
          <h2 style={{ marginTop: 0 }}>{t.eventDetail.policySection}</h2>

          {activePolicy ? (
            <p>
              <strong>{activePolicy.version}</strong> · {activePolicy.language.toUpperCase()} —{' '}
              {activePolicy.title}
            </p>
          ) : (
            <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.noPolicy}</p>
          )}

          {publishedPolicies.length === 0 ? (
            <p style={{ color: 'var(--acs-color-text-muted)' }}>
              {t.eventDetail.noPublishedPolicy}
            </p>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label className="acs-field" style={{ marginBottom: 0, flex: '1 1 18rem' }}>
                <span className="acs-field__label">{t.eventDetail.choosePolicy}</span>
                <select
                  className="acs-input"
                  value={selectedPolicy}
                  onChange={(e) => setSelectedPolicy(e.target.value)}
                >
                  <option value="">—</option>
                  {publishedPolicies.map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.version} ({policy.language.toUpperCase()}) — {policy.title}
                    </option>
                  ))}
                </select>
              </label>
              <AcsButton
                disabled={selectedPolicy === '' || updateEvent.isPending}
                onClick={() => updateEvent.mutate({ activePrivacyPolicyId: selectedPolicy })}
              >
                {t.eventDetail.applyPolicy}
              </AcsButton>
            </div>
          )}
        </AcsCard>

        {/* ------------------------------------------------------ Stato -- */}
        <AcsCard>
          <h2 style={{ marginTop: 0 }}>{t.eventDetail.statusSection}</h2>
          <p style={{ color: 'var(--acs-color-text-muted)' }}>
            {detail.status === 'ACTIVE'
              ? t.eventDetail.activeHint
              : detail.status === 'CLOSED'
                ? t.eventDetail.closedHint
                : t.eventDetail.draftHint}
          </p>

          {/* Il vincolo è applicato dal server; qui se ne spiega il motivo,
              invece di lasciare un pulsante inerte senza spiegazione. */}
          {!canActivate && detail.status !== 'ACTIVE' ? (
            <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.policyRequired}</p>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
            {detail.status !== 'ACTIVE' ? (
              <AcsButton
                disabled={!canActivate || updateEvent.isPending}
                onClick={() => updateEvent.mutate({ status: 'ACTIVE' })}
              >
                {t.eventDetail.activate}
              </AcsButton>
            ) : (
              <AcsButton
                variant="secondary"
                disabled={updateEvent.isPending}
                onClick={() => updateEvent.mutate({ status: 'CLOSED' })}
              >
                {t.eventDetail.close}
              </AcsButton>
            )}
          </div>
        </AcsCard>

        {/* ----------------------------------------------------- Tablet -- */}
        <AcsCard>
          <h2 style={{ marginTop: 0 }}>{t.eventDetail.devicesSection}</h2>

          {issuedToken ? (
            <div
              style={{
                border: '1px solid var(--acs-color-border)',
                borderRadius: 'var(--acs-radius-md)',
                padding: 'var(--acs-space-4)',
                marginBottom: 'var(--acs-space-4)',
              }}
            >
              <strong>{t.eventDetail.tokenTitle}</strong>
              <p
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 'var(--acs-font-size-lg)',
                  wordBreak: 'break-all',
                  margin: 'var(--acs-space-2) 0',
                }}
              >
                {issuedToken}
              </p>
              <p style={{ color: 'var(--acs-color-text-muted)', marginTop: 0 }}>
                {t.eventDetail.tokenOnce}
              </p>
              <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
                <AcsButton
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard?.writeText(issuedToken).then(() => setCopied(true));
                  }}
                >
                  {copied ? t.eventDetail.tokenCopied : t.eventDetail.tokenCopy}
                </AcsButton>
                <AcsButton onClick={() => setIssuedToken(null)}>
                  {t.eventDetail.tokenDone}
                </AcsButton>
              </div>
            </div>
          ) : null}

          {devices.data?.devices.length === 0 ? (
            <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.noDevices}</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 var(--acs-space-4)' }}>
              {devices.data?.devices.map((device) => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  onRevoke={() => {
                    if (window.confirm(t.eventDetail.revokeConfirm)) revokeDevice.mutate(device.id);
                  }}
                  onReissue={() => {
                    if (window.confirm(t.eventDetail.reissueConfirm)) reissueToken.mutate(device.id);
                  }}
                />
              ))}
            </ul>
          )}

          <form
            onSubmit={handleRegisterDevice}
            style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}
          >
            <div style={{ flex: '1 1 16rem' }}>
              <AcsInput
                label={t.eventDetail.deviceLabel}
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                autoComplete="off"
              />
            </div>
            <AcsButton type="submit" disabled={registerDevice.isPending}>
              {t.eventDetail.addDevice}
            </AcsButton>
          </form>
        </AcsCard>
      </div>
    </AcsPageShell>
  );
}

function DeviceRow({
  device,
  onRevoke,
  onReissue,
}: {
  device: DeviceSummary;
  onRevoke: () => void;
  onReissue: () => void;
}) {
  const { t } = useI18n();

  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--acs-space-4)',
        flexWrap: 'wrap',
        padding: 'var(--acs-space-3) 0',
        borderBottom: '1px solid var(--acs-color-border)',
      }}
    >
      <div>
        <strong>{device.label}</strong>
        <p style={{ margin: 0, color: 'var(--acs-color-text-muted)' }}>
          {t.eventDetail.deviceStatus[device.status]} · {t.eventDetail.lastSeen}:{' '}
          {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : t.eventDetail.never}
        </p>
      </div>
      {device.status !== 'REVOKED' ? (
        <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
          <AcsButton variant="secondary" onClick={onReissue}>
            {t.eventDetail.reissue}
          </AcsButton>
          <AcsButton variant="secondary" onClick={onRevoke}>
            {t.eventDetail.revokeDevice}
          </AcsButton>
        </div>
      ) : null}
    </li>
  );
}


/**
 * Immagine della fiera (§6.2, §20.6).
 *
 * L'alt text è un campo obbligatorio del modulo, non un extra: senza,
 * l'immagine è invisibile a chi usa uno screen reader. Il server la rifiuta
 * comunque, ma bloccare qui evita un viaggio di rete per dirlo.
 */
function BrandingSection({
  event,
  onChanged,
}: {
  event: EventDetail;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState(event.branding.imageAltText ?? '');
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => uploadEventBanner(event.id, file as File, altText),
    onSuccess: () => {
      setFile(null);
      setError(null);
      onChanged();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const remove = useMutation({
    mutationFn: () => clearEventBanner(event.id),
    onSuccess: () => {
      setAltText('');
      setError(null);
      onChanged();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  function submit(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (!file) return setError(t.eventDetail.bannerMissingFile);
    if (altText.trim().length === 0) return setError(t.eventDetail.bannerMissingAlt);
    upload.mutate();
  }

  return (
    <AcsCard>
      <h2 style={{ marginTop: 0 }}>{t.eventDetail.bannerSection}</h2>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.bannerHint}</p>

      {event.branding.imageAssetId ? (
        <figure style={{ margin: '0 0 var(--acs-space-4)' }}>
          <img
            src={`/api/assets/${event.branding.imageAssetId}`}
            alt={event.branding.imageAltText ?? ''}
            style={{
              width: '100%',
              maxHeight: '10rem',
              objectFit: 'cover',
              borderRadius: 'var(--acs-radius-md)',
              display: 'block',
            }}
          />
          <figcaption style={{ color: 'var(--acs-color-text-muted)', marginTop: 'var(--acs-space-2)' }}>
            {t.eventDetail.bannerPrivacyNote}
          </figcaption>
        </figure>
      ) : (
        <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.bannerNone}</p>
      )}

      <form onSubmit={submit} className="acs-stack">
        <div>
          <AcsInput
            label={t.eventDetail.bannerAltLabel}
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            required
          />
          <p style={{ color: 'var(--acs-color-text-muted)', marginTop: 'var(--acs-space-2)' }}>
            {t.eventDetail.bannerAltHint}
          </p>
        </div>

        <label className="acs-field">
          <span className="acs-field__label">{t.eventDetail.bannerFileLabel}</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
          />
        </label>

        {error ? (
          <p className="acs-field__error" role="alert">
            {error}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
          <AcsButton type="submit" disabled={upload.isPending}>
            {event.branding.imageAssetId ? t.eventDetail.bannerReplace : t.eventDetail.bannerUpload}
          </AcsButton>
          {event.branding.imageAssetId ? (
            <AcsButton
              type="button"
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t.eventDetail.bannerRemove}
            </AcsButton>
          ) : null}
        </div>
      </form>
    </AcsCard>
  );
}

/**
 * Voci richiedibili della fiera (§19).
 *
 * Personalizza **che cosa si può chiedere**, mai a che cosa si acconsente: il
 * consenso marketing resta legato al proprio testo pubblicato e il ricontatto
 * alla propria casella. Ogni voce ricade su un `type` del vocabolario, quindi
 * su una finalità già configurata — due voci dello stesso tipo restano un solo
 * trattamento, con un solo periodo di conservazione.
 *
 * Non esiste un controllo per preselezionare una voce, qui come nel modello:
 * lo schema del server è `strict()` e rifiuterebbe il campo.
 */
function RequestableItemsSection({
  event,
  onChanged,
}: {
  event: EventDetail;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<RequestableItem[]>(() =>
    event.requestableItems.map((item) => ({ ...item, labels: { ...item.labels } })),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const defaultLanguage = event.languages[0] ?? 'it';
  // Gli identificatori già salvati non si toccano: finiscono su ogni richiesta
  // raccolta, e cambiarli slegherebbe lo storico dalla voce corrente.
  const persisted = new Set(event.requestableItems.map((item) => item.id));

  const save = useMutation({
    mutationFn: () => api.updateEvent(event.id, { requestableItems: items }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onChanged();
    },
    onError: (cause: Error) => {
      setSaved(false);
      setError(cause.message);
    },
  });

  function slugify(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  function update(index: number, patch: Partial<RequestableItem>): void {
    setSaved(false);
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  }

  function updateLabel(index: number, language: string, value: string): void {
    setSaved(false);
    setItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, labels: { ...item.labels, [language]: value } } : item,
      ),
    );
  }

  function submit(formEvent: FormEvent): void {
    formEvent.preventDefault();
    if (items.length === 0) return setError(t.eventDetail.itemsAtLeastOne);

    // L'identificatore delle voci nuove si deriva dall'etichetta predefinita:
    // così non va inventato a mano e resta leggibile negli export.
    const prepared = items.map((item) =>
      persisted.has(item.id) ? item : { ...item, id: slugify(item.labels[defaultLanguage] ?? '') },
    );
    if (prepared.some((item) => item.id.length === 0)) {
      return setError(t.eventDetail.itemsMissingLabel);
    }

    setItems(prepared);
    save.mutate();
  }

  return (
    <AcsCard>
      <h2 style={{ marginTop: 0 }}>{t.eventDetail.itemsSection}</h2>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.itemsHint}</p>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.itemsNoPreselect}</p>

      <form onSubmit={submit} className="acs-stack">
        {items.map((item, index) => (
          <fieldset
            key={`${item.id}-${index}`}
            style={{
              border: '1px solid var(--acs-color-border)',
              borderRadius: 'var(--acs-radius-md)',
              padding: 'var(--acs-space-4)',
            }}
          >
            <legend style={{ padding: '0 var(--acs-space-2)' }}>
              {persisted.has(item.id) ? item.id : t.eventDetail.itemsAdd}
            </legend>

            <label className="acs-field">
              <span className="acs-field__label">{t.eventDetail.itemsType}</span>
              <select
                className="acs-field__control"
                value={item.type}
                onChange={(e) =>
                  update(index, { type: e.target.value as RequestableItem['type'] })
                }
              >
                <option value="DOCUMENT">DOCUMENT</option>
                <option value="CATALOG">CATALOG</option>
                <option value="PRODUCT_INFO">PRODUCT_INFO</option>
              </select>
            </label>

            {event.languages.map((language) => (
              <AcsInput
                key={language}
                label={`${t.eventDetail.itemsLabelFor} (${language.toUpperCase()})`}
                value={item.labels[language] ?? ''}
                onChange={(e) => updateLabel(index, language, e.target.value)}
                required={language === defaultLanguage}
              />
            ))}

            <div style={{ marginTop: 'var(--acs-space-3)' }}>
              <AcsButton
                type="button"
                variant="secondary"
                onClick={() => {
                  setSaved(false);
                  setItems((current) => current.filter((_, i) => i !== index));
                }}
              >
                {t.eventDetail.itemsRemove}
              </AcsButton>
            </div>
          </fieldset>
        ))}

        {error ? (
          <p className="acs-field__error" role="alert">
            {error}
          </p>
        ) : null}
        {saved ? <p role="status">{t.eventDetail.itemsSaved}</p> : null}

        <p style={{ color: 'var(--acs-color-text-muted)', margin: 0 }}>
          {t.eventDetail.itemsRemovedWarning}
        </p>

        <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
          <AcsButton
            type="button"
            variant="secondary"
            onClick={() => {
              setSaved(false);
              setItems((current) => [
                ...current,
                { id: '', type: 'DOCUMENT', labels: {} },
              ]);
            }}
          >
            {t.eventDetail.itemsAdd}
          </AcsButton>
          <AcsButton type="submit" disabled={save.isPending}>
            {t.eventDetail.itemsSave}
          </AcsButton>
        </div>
      </form>
    </AcsCard>
  );
}

/**
 * Etichetta della casella di ricontatto commerciale (§2.2, §19).
 *
 * Esiste perché il commerciale possa formulare la richiesta come serve al
 * proprio pubblico senza ricorrere a una voce dell'elenco che *imiti* il
 * ricontatto ricadendo però su un'altra finalità — con la conseguenza che chi
 * la spunta non compare nell'export del ricontatto.
 *
 * Ciò che la personalizzazione non tocca è la finalità: comunque sia scritta,
 * la spunta registra una richiesta COMMERCIAL_CALLBACK. È la differenza fra
 * cambiare un'etichetta e aprire una seconda via verso un'altra finalità.
 */
function CallbackLabelSection({
  event,
  onChanged,
}: {
  event: EventDetail;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [labels, setLabels] = useState<Record<string, string>>(
    () => ({ ...(event.callbackLabels ?? {}) }),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (next: Record<string, string> | null) =>
      api.updateEvent(event.id, { callbackLabels: next }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onChanged();
    },
    onError: (cause: Error) => {
      setSaved(false);
      setError(cause.message);
    },
  });

  function submit(formEvent: FormEvent): void {
    formEvent.preventDefault();
    // Solo le lingue effettivamente compilate: una stringa vuota non è
    // un'etichetta, e lasciarla passare mostrerebbe una casella senza testo.
    const filled = Object.fromEntries(
      Object.entries(labels).filter(([, value]) => value.trim().length > 0),
    );
    save.mutate(Object.keys(filled).length === 0 ? null : filled);
  }

  return (
    <AcsCard>
      <h2 style={{ marginTop: 0 }}>{t.eventDetail.callbackSection}</h2>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.callbackHint}</p>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.callbackConstraint}</p>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.eventDetail.callbackFixed}</p>

      <p style={{ color: 'var(--acs-color-text-muted)' }}>
        {t.eventDetail.callbackDefault} <em>{t.kiosk.callback}</em>
      </p>

      <form onSubmit={submit} className="acs-stack">
        {event.languages.map((language) => (
          <AcsInput
            key={language}
            label={`${t.eventDetail.callbackSection} (${language.toUpperCase()})`}
            value={labels[language] ?? ''}
            onChange={(e) => {
              setSaved(false);
              setLabels((current) => ({ ...current, [language]: e.target.value }));
            }}
          />
        ))}

        {error ? (
          <p className="acs-field__error" role="alert">
            {error}
          </p>
        ) : null}
        {saved ? <p role="status">{t.eventDetail.callbackSaved}</p> : null}

        <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
          <AcsButton type="submit" disabled={save.isPending}>
            {t.eventDetail.callbackSave}
          </AcsButton>
          <AcsButton
            type="button"
            variant="secondary"
            disabled={save.isPending}
            onClick={() => {
              setLabels({});
              setSaved(false);
              save.mutate(null);
            }}
          >
            {t.eventDetail.callbackReset}
          </AcsButton>
        </div>
      </form>
    </AcsCard>
  );
}
